import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase';

export interface CostItem {
  id: string;
  label: string;
  description: string;
  cost: number;
  isLabor?: boolean;
  isMaterials?: boolean;
  isOther?: boolean;
  isIncluded?: boolean; // Field to control inclusion in totals
  isPaid?: boolean; // Field to track if item has been paid for
  createdAt?: Date;
  updatedAt?: Date;
}

export interface BudgetData {
  id?: string;
  userId: string;
  totalBudget: number;
  costItems: CostItem[];
  createdAt?: any;
  updatedAt?: any;
}

const COLLECTION_NAME = 'homebuildbudget';

/**
 * Get budget data for a specific user
 */
export const getBudgetData = async (userId: string): Promise<BudgetData | null> => {
  try {
    const budgetDocRef = doc(db, COLLECTION_NAME, userId);
    const budgetDoc = await getDoc(budgetDocRef);
    
    if (budgetDoc.exists()) {
      return {
        id: budgetDoc.id,
        ...budgetDoc.data()
      } as BudgetData;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching budget data:', error);
    throw new Error('Failed to fetch budget data');
  }
};

/**
 * Save complete budget data for a user
 */
export const saveBudgetData = async (userId: string, budgetData: Omit<BudgetData, 'id' | 'userId'>): Promise<void> => {
  try {
    const budgetDocRef = doc(db, COLLECTION_NAME, userId);
    const timestamp = serverTimestamp();
    const now = new Date();
    
    const dataToSave: Omit<BudgetData, 'id'> = {
      userId,
      totalBudget: budgetData.totalBudget,
      costItems: budgetData.costItems.map(item => ({
        ...item,
        updatedAt: now // Use regular Date for items in array
      })),
      updatedAt: timestamp, // Use serverTimestamp only at document level
      createdAt: budgetData.createdAt || timestamp
    };
    
    await setDoc(budgetDocRef, dataToSave, { merge: true });
  } catch (error) {
    console.error('Error saving budget data:', error);
    throw new Error('Failed to save budget data');
  }
};

/**
 * Update just the total budget for a user
 */
export const updateTotalBudget = async (userId: string, totalBudget: number): Promise<void> => {
  try {
    const budgetDocRef = doc(db, COLLECTION_NAME, userId);
    await updateDoc(budgetDocRef, {
      totalBudget,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating total budget:', error);
    throw new Error('Failed to update total budget');
  }
};

/**
 * Add a new cost item
 */
export const addCostItem = async (userId: string, costItem: Omit<CostItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<CostItem> => {
  try {
    const budgetDocRef = doc(db, COLLECTION_NAME, userId);
    const budgetDoc = await getDoc(budgetDocRef);
    
    const now = new Date();
    const newCostItem: CostItem = {
      ...costItem,
      id: Date.now().toString(), // Simple ID generation
      isLabor: costItem.isLabor || false,
      isMaterials: costItem.isMaterials || false,
      isOther: costItem.isOther || false,
      isIncluded: true, // Default new items to be included in calculations
      isPaid: false, // Default new items to not paid
      createdAt: now,
      updatedAt: now
    };
    
    if (budgetDoc.exists()) {
      const currentData = budgetDoc.data() as BudgetData;
      const updatedCostItems = [...(currentData.costItems || []), newCostItem];
      
      await updateDoc(budgetDocRef, {
        costItems: updatedCostItems,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new budget document if it doesn't exist
      const newBudgetData: Omit<BudgetData, 'id'> = {
        userId,
        totalBudget: 0,
        costItems: [newCostItem],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(budgetDocRef, newBudgetData);
    }
    
    return newCostItem;
  } catch (error) {
    console.error('Error adding cost item:', error);
    throw new Error('Failed to add cost item');
  }
};

/**
 * Remove a cost item
 */
export const removeCostItem = async (userId: string, costItemId: string): Promise<void> => {
  try {
    const budgetDocRef = doc(db, COLLECTION_NAME, userId);
    const budgetDoc = await getDoc(budgetDocRef);
    
    if (budgetDoc.exists()) {
      const currentData = budgetDoc.data() as BudgetData;
      const updatedCostItems = currentData.costItems.filter(item => item.id !== costItemId);
      
      await updateDoc(budgetDocRef, {
        costItems: updatedCostItems,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error removing cost item:', error);
    throw new Error('Failed to remove cost item');
  }
};

/**
 * Initialize empty budget for a new user
 */
export const initializeBudget = async (userId: string): Promise<BudgetData> => {
  try {
    const budgetDocRef = doc(db, COLLECTION_NAME, userId);
    const timestamp = serverTimestamp();
    
    const initialBudgetData: Omit<BudgetData, 'id'> = {
      userId,
      totalBudget: 0,
      costItems: [],
      createdAt: timestamp,
      updatedAt: timestamp
    };
    
    await setDoc(budgetDocRef, initialBudgetData);
    
    return {
      id: userId,
      ...initialBudgetData
    };
  } catch (error) {
    console.error('Error initializing budget:', error);
    throw new Error('Failed to initialize budget');
  }
};